import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import {
  Star,
  MapPin,
  Phone,
  Video,
  Calendar,
  User,
  LogOut,
  Menu,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Users,
  Shield,
  Crown,
  History,
  BarChart3,
  MessageCircle,
  Heart,
  Smile
} from "lucide-react";
import SafetyGate from "../components/SafetyGate";
import ResponderRoleReminder from "../components/ResponderRoleReminder";
import PeerSupportDisclaimer from "../components/PeerSupportDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestType, setRequestType] = useState("in_person");
  const [requestDescription, setRequestDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Refresh user data on mount to get latest training status
  useEffect(() => {
    const refreshUserData = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (setUser && response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error("Error refreshing user data:", error);
      }
    };
    refreshUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  useEffect(() => {
    loadDashboardData();
    getUserLocation();
  }, [user]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  };

  const loadDashboardData = async () => {
    try {
      const activeRes = await axios.get(`${API}/requests/active`);
      setActiveRequest(activeRes.data);

      const aptsRes = await axios.get(`${API}/appointments`);
      const upcomingApts = aptsRes.data
        .filter(apt => apt.status === "scheduled" && new Date(apt.scheduled_at) > new Date())
        .slice(0, 3);
      setAppointments(upcomingApts);

      if (user?.role === "responder") {
        const pendingRes = await axios.get(`${API}/requests/pending`);
        setPendingRequests(pendingRes.data);
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/");
    }
  };

  const handleCreateRequest = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/requests`, {
        request_type: requestType,
        location: userLocation,
        description: requestDescription
      });
      setActiveRequest(response.data);
      setShowRequestDialog(false);
      setRequestDescription("");
      toast.success("Request sent! A responder will be with you shortly.");
      navigate(`/request/${response.data.request_id}`);
    } catch (error) {
      toast.error("Failed to create request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    // Check if training is completed
    if (!user?.training_completed) {
      toast.error("Please complete the Volunteer Training before accepting requests.");
      navigate("/volunteer-training");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/requests/${requestId}/accept`);
      toast.success("Request accepted! Navigate to the veteran.");
      navigate(`/request/${requestId}`);
    } catch (error) {
      toast.error("Failed to accept request.");
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const isVeteran = user?.role === "veteran" || user?.role === "pending_responder";
  const isApprovedResponder = user?.role === "responder";
  const isResponder = isApprovedResponder;
  const isAdmin = user?.is_admin;

  const navItems = [
    { icon: Star, label: "Dashboard", path: "/dashboard", active: true },
    { icon: Users, label: "Responders", path: "/responders" },
    { icon: MessageCircle, label: "Chats", path: "/chat-history" },
    { icon: Calendar, label: "Appointments", path: "/appointments" },
    { icon: BookOpen, label: "Resources", path: "/resources" },
    { icon: User, label: "Profile", path: "/profile" },
    ...(isVeteran && !isApprovedResponder ? [{ icon: History, label: "History", path: "/request-history" }] : []),
    ...(isVeteran ? [{ icon: Heart, label: "Follow-ups", path: "/follow-ups" }] : []),
    ...(isVeteran ? [{ icon: Smile, label: "Mood", path: "/mood-tracking" }] : []),
    ...(isVeteran ? [{ icon: Users, label: "Groups", path: "/support-groups" }] : []),
    ...(isApprovedResponder ? [{ icon: BarChart3, label: "My Impact", path: "/analytics" }] : []),
    ...(isApprovedResponder ? [{ icon: Heart, label: "Peer Support", path: "/peer-support" }] : []),
    ...(isApprovedResponder ? [{ icon: Shield, label: "Training", path: "/volunteer-training" }] : []),
    ...(isAdmin ? [{ icon: Crown, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Safety Gate Modal */}
      <SafetyGate user={user} />
      
      {/* Top Blue Bar */}
      <div className="va-blue-bar h-1 fixed top-0 left-0 right-0 z-50"></div>
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-1 left-0 right-0 bg-white border-b border-border z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-border z-50
        transform transition-transform duration-200 ease-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 pb-24 h-full overflow-y-auto">
          <div className="flex items-center gap-2 mb-8">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-12 w-auto" />
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
            </span>
          </div>

          {/* User Info */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-6">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-primary text-white">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-foreground truncate">{user?.name}</p>
              <Badge variant={isVeteran ? "secondary" : "default"} className="text-xs">
                {isVeteran ? "Veteran" : "Responder"}
              </Badge>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors touch-target
                  ${item.active 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-1 min-h-screen">
        <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
          {/* Welcome Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Welcome back, {user?.name?.split(" ")[0]}
              </h1>
              <p className="text-muted-foreground mt-1">
                {isVeteran ? "We're here to support you." : "Ready to help our Veterans."}
              </p>
            </div>
            {!isVeteran && (
              <Badge 
                variant={user?.is_available ? "default" : "secondary"}
                className="self-start"
              >
                {user?.is_available ? "Available" : "Unavailable"}
              </Badge>
            )}
          </div>

          {/* Active Request Banner */}
          {activeRequest && (
            <Card className="bg-secondary/10 border-secondary/30 card-valor" data-testid="active-request-banner">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="status-dot status-dot-pending"></div>
                  <div>
                    <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                      Active Request
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {activeRequest.status === "pending" && "Waiting for responder..."}
                      {activeRequest.status === "accepted" && "Responder is on the way"}
                      {activeRequest.status === "in_progress" && "Support in progress"}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate(`/request/${activeRequest.request_id}`)}
                  className="bg-secondary hover:bg-secondary/90"
                  data-testid="view-request-btn"
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Role Reminder for Responders */}
          {isResponder && (
            <ResponderRoleReminder data-testid="responder-role-reminder" />
          )}

          {/* Training Required Banner for Approved Responders */}
          {isApprovedResponder && !user?.training_completed && (
            <Card className="bg-amber-50 border-amber-200 card-valor" data-testid="training-required-banner">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800" style={{ fontFamily: 'Manrope' }}>
                      Training Required
                    </p>
                    <p className="text-sm text-amber-700">
                      Complete the Volunteer Role & Safety Training to start accepting requests.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/volunteer-training")}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="start-training-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Start Training
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Peer Support Button (Veterans Only) */}
          {isVeteran && !activeRequest && (
            <Card className="bg-white border-0 card-valor overflow-hidden">
              <CardContent className="p-6 sm:p-8 text-center space-y-6">
                <div className="space-y-2">
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    Need Peer Support?
                  </h2>
                  <p className="text-muted-foreground">
                    Connect with a veteran peer who understands your journey
                  </p>
                </div>
                <Button 
                  className="crisis-button w-full sm:w-auto rounded-lg bg-secondary hover:bg-secondary/90"
                  size="lg"
                  onClick={() => setShowRequestDialog(true)}
                  data-testid="request-help-btn"
                >
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Get Veteran Peer Support
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Become a Responder Card (Veterans Only, not already applied) */}
          {user?.role === "veteran" && (
            <Card className="card-valor bg-primary/5 border-primary/20">
              <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                      Want to Help Fellow Veterans?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Become a VALORLink™ peer responder and make a difference
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/apply-responder")}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="become-responder-btn"
                >
                  Apply Now
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pending Application Status */}
          {user?.role === "pending_responder" && (
            <Card className="card-valor bg-yellow-50 border-yellow-200">
              <CardContent className="p-6 flex items-center gap-4">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div>
                  <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    Application Under Review
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your responder application is being reviewed by our team. We'll notify you once it's approved.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Requests (Responders Only) */}
          {isApprovedResponder && pendingRequests.length > 0 && (
            <Card className="card-valor" data-testid="pending-requests-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <AlertCircle className="w-5 h-5 text-secondary" />
                  Pending Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {pendingRequests.map((request) => (
                  <div 
                    key={request.request_id}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={request.veteran_picture} />
                        <AvatarFallback className="bg-primary text-white">{getInitials(request.veteran_name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{request.veteran_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {request.request_type === "in_person" ? (
                            <><MapPin className="w-4 h-4" /> In-Person</>
                          ) : (
                            <><Phone className="w-4 h-4" /> Phone/Video</>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleAcceptRequest(request.request_id)}
                      className="bg-primary hover:bg-primary/90"
                      data-testid={`accept-request-${request.request_id}`}
                    >
                      Accept
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Quick Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Upcoming Appointments */}
            <Card className="card-valor bg-white border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Calendar className="w-4 h-4 text-primary" />
                  Upcoming
                </CardTitle>
              </CardHeader>
              <CardContent>
                {appointments.length > 0 ? (
                  <div className="space-y-2">
                    {appointments.map((apt) => (
                      <div key={apt.appointment_id} className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-foreground">
                          {new Date(apt.scheduled_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No upcoming appointments</p>
                )}
                <Button 
                  variant="link" 
                  className="px-0 mt-2 text-primary"
                  onClick={() => navigate("/appointments")}
                  data-testid="view-appointments-btn"
                >
                  View All
                </Button>
              </CardContent>
            </Card>

            {/* Resources Card */}
            <Card className="card-valor bg-white border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <BookOpen className="w-4 h-4 text-primary" />
                  Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">
                  Access crisis hotlines and helpful resources
                </p>
                <Button 
                  variant="link" 
                  className="px-0 text-primary"
                  onClick={() => navigate("/resources")}
                  data-testid="view-resources-btn"
                >
                  View Resources
                </Button>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card className="card-valor bg-white border-0 sm:col-span-2 lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Your Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Role</span>
                    <Badge variant="secondary">{user?.role}</Badge>
                  </div>
                  {!isVeteran && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Availability</span>
                      <span className={user?.is_available ? "text-green-600 font-medium" : "text-muted-foreground"}>
                        {user?.is_available ? "Online" : "Offline"}
                      </span>
                    </div>
                  )}
                </div>
                <Button 
                  variant="link" 
                  className="px-0 mt-2 text-primary"
                  onClick={() => navigate("/profile")}
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Request Help Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="sm:max-w-md" data-testid="request-help-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Request Support</DialogTitle>
            <DialogDescription>
              Choose how you'd like to receive support from a peer responder.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <Label className="label-uppercase">Support Type</Label>
              <RadioGroup value={requestType} onValueChange={setRequestType}>
                <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="in_person" id="in_person" />
                  <Label htmlFor="in_person" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-secondary" />
                      <span className="font-medium">In-Person Support</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      A responder will come to your location
                    </p>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted cursor-pointer">
                  <RadioGroupItem value="call" id="call" />
                  <Label htmlFor="call" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-secondary" />
                      <span className="font-medium">Phone/Video Call</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Connect via phone or video call
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label htmlFor="description" className="label-uppercase">
                What's going on? (Optional)
              </Label>
              <Textarea
                id="description"
                placeholder="Share anything that might help your responder support you better..."
                value={requestDescription}
                onChange={(e) => setRequestDescription(e.target.value)}
                className="min-h-[100px]"
                data-testid="request-description"
              />
            </div>

            {requestType === "in_person" && (
              <div className="p-3 bg-muted rounded-lg flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Location Sharing</p>
                  <p className="text-sm text-muted-foreground">
                    {userLocation 
                      ? "Your location will be shared with the responder"
                      : "Please enable location services for in-person support"
                    }
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRequest}
              disabled={isSubmitting || (requestType === "in_person" && !userLocation)}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="submit-request-btn"
            >
              {isSubmitting ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Peer Support Disclaimer Footer */}
      <footer className="lg:ml-64 mt-auto">
        <PeerSupportDisclaimer />
      </footer>
    </div>
  );
};

export default Dashboard;
